
// Create a function to get a color based on category
export const scheduleHeaderHeigth = 58;
export const scheduleHeaderHeigthMobile = 45;

export const scheduleSlotsHeigth = 65;
export const scheduleSlotsHeigthMobile = 65; // Kept for backward compatibility

/**
 * Calcule la hauteur d'un slot mobile en fonction de la durée du slot
 * pour que la représentation visuelle soit proportionnelle au temps réel
 * @param slotDuration - Durée d'un créneau en minutes
 * @returns Hauteur en pixels
 */
export const getScheduleSlotsHeightMobile = (slotDuration: number): number => {
  // Cas spécial pour 30 minutes (demande utilisateur)
  if (slotDuration === 30) {
    return 35;
  }
  
  // Référence : 45 min = 65px (valeur historique)
  const PIXELS_PER_MINUTE = 65 / 45; // ≈ 1.44 px/min
  const MIN_HEIGHT = 45; // Minimum pour garder lisibilité
  const MAX_HEIGHT = 90; // Maximum pour éviter trop grand
  
  const calculatedHeight = Math.round(slotDuration * PIXELS_PER_MINUTE);
  return Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, calculatedHeight));
};

// Define days of the week
export const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

/**
 * Jours ouverts par défaut aux réservations et à l'affichage du planning.
 * 0 = Dimanche, 1 = Lundi, ..., 6 = Samedi.
 * Défaut : Lundi -> Samedi (comportement historique).
 */
export const DEFAULT_OPEN_DAYS_OF_WEEK: number[] = [1, 2, 3, 4, 5, 6];

/**
 * Vérifie si un jour (Date) est ouvert selon la configuration.
 */
export const isDayOpen = (date: Date, openDaysOfWeek?: number[] | null): boolean => {
  const list = openDaysOfWeek && openDaysOfWeek.length > 0 ? openDaysOfWeek : DEFAULT_OPEN_DAYS_OF_WEEK;
  return list.includes(date.getDay());
};

// Function to convert 24h time to 12h time
export const convertTo12Hour = (time: string) => {
  const [hour, minute] = time.split(':').map(Number);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
};

// Function to calculate class height and position based on time
export const calculateClassPositionAndHeight = (startTime: string, duration: number) => {
  const [hour, minute] = startTime.split(':').map(Number);
  const hourDecimal = hour + minute / 60;
  
  // Assuming the day starts at 6:00 and each hour is 55px tall (increased from 50px)
  const startPosition = (hourDecimal - 6) * 55;
  const height = (duration / 60) * 55;
  
  return {
    top: `${startPosition}px`,
    height: `${height}px`
  };
};

// Helper pour convertir une heure en minutes depuis minuit
const parseTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

// Type pour les créneaux traités (slot normal ou gap)
export type ProcessedSlot = 
  | { type: 'slot'; time: string }
  | { type: 'gap'; startTime: string; endTime: string; slotsCount: number };

/**
 * Traite les créneaux horaires pour regrouper les créneaux vides consécutifs en gaps
 * Tient compte de la durée des cours pour déterminer si un créneau est couvert
 * @param timeSlots - Liste des créneaux horaires
 * @param weekDates - Dates de la semaine
 * @param getClassForTimeSlot - Fonction pour obtenir un cours à un créneau (retourne le cours qui COMMENCE à ce créneau)
 * @param isAdmin - Si l'utilisateur est admin (pour garder les créneaux vides en fin)
 * @param slotDuration - Durée d'un créneau en minutes (pour calculer les slots couverts)
 * @returns Liste de créneaux traités avec gaps
 */
export const processTimeSlotsWithGaps = (
  timeSlots: string[],
  weekDates: Date[],
  getClassForTimeSlot: (date: Date, time: string) => any,
  isAdmin: boolean,
  slotDuration: number = 45
): ProcessedSlot[] => {
  // ADMINS : Pas de gaps, afficher tous les créneaux pour pouvoir ajouter des cours
  if (isAdmin) {
    return timeSlots.map(time => ({ type: 'slot' as const, time }));
  }

  // 1. Pré-calculer tous les créneaux couverts par des cours (en tenant compte de leur durée)
  const coveredSlots = new Set<string>();
  
  for (const date of weekDates) {
    for (const time of timeSlots) {
      const classInfo = getClassForTimeSlot(date, time);
      if (classInfo) {
        // Ce créneau a un cours qui commence ici
        const classDuration = parseInt(classInfo.duration) || slotDuration;
        const classStartMinutes = parseTimeToMinutes(time);
        const classEndMinutes = classStartMinutes + classDuration;
        
        // Marquer tous les créneaux couverts par ce cours
        for (const slot of timeSlots) {
          const slotMinutes = parseTimeToMinutes(slot);
          // Un créneau est couvert si: classStart <= slotStart < classEnd
          if (slotMinutes >= classStartMinutes && slotMinutes < classEndMinutes) {
            coveredSlots.add(slot);
          }
        }
      }
    }
  }

  // 2. Traiter les créneaux pour créer les gaps
  const result: ProcessedSlot[] = [];
  let gapStartIndex: number | null = null;
  let gapCount = 0;

  for (let i = 0; i < timeSlots.length; i++) {
    const time = timeSlots[i];
    const isCovered = coveredSlots.has(time);

    if (isCovered) {
      // Fermer un gap précédent s'il existe
      if (gapStartIndex !== null && gapCount > 0) {
        result.push({ 
          type: 'gap', 
          startTime: timeSlots[gapStartIndex], 
          endTime: time,  // Le gap se termine juste avant ce créneau
          slotsCount: gapCount 
        });
        gapStartIndex = null;
        gapCount = 0;
      }
      result.push({ type: 'slot', time });
    } else {
      // Début ou continuation d'un gap
      if (gapStartIndex === null) {
        gapStartIndex = i;
      }
      gapCount++;
    }
  }

  // Gérer un gap final (si la journée se termine par des créneaux vides)
  if (gapStartIndex !== null && gapCount > 0) {
    if (isAdmin) {
      // Admins voient les derniers créneaux vides pour pouvoir ajouter des cours
      for (let i = gapStartIndex; i < timeSlots.length; i++) {
        result.push({ type: 'slot', time: timeSlots[i] });
      }
    } else {
      // Clients voient un indicateur de gap (seulement si plus d'1 créneau vide)
      if (gapCount > 1) {
        result.push({ 
          type: 'gap', 
          startTime: timeSlots[gapStartIndex], 
          endTime: timeSlots[timeSlots.length - 1],
          slotsCount: gapCount 
        });
      } else {
        // Un seul créneau vide à la fin, pas besoin de gap
        result.push({ type: 'slot', time: timeSlots[gapStartIndex] });
      }
    }
  }

  return result;
};

/** Single source of truth for weekly grid row heights (time column + day columns). */
export const getWeeklySlotHeight = (isMobile: boolean, compact: boolean, slotDuration: number): number => {
  if (isMobile) {
    const base = getScheduleSlotsHeightMobile(slotDuration);
    return compact ? Math.max(56, Math.round(base * 0.95)) : base;
  }
  return compact ? 70 : scheduleSlotsHeigth;
};

export const getWeeklyHeaderHeight = (isMobile: boolean, compact: boolean): number =>
  compact ? (isMobile ? 42 : 50) : (isMobile ? scheduleHeaderHeigthMobile : scheduleHeaderHeigth);
